
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  SlidersHorizontal, 
  X, 
  Filter, 
  Star, 
  Crown,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Palette,
  Award,
  TrendingUp
} from "lucide-react";
import { Category } from "@/types/common";

interface AdvancedLuxuryFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  categories?: Category[];
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
}

const AdvancedLuxuryFilters = ({
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  sortBy,
  setSortBy,
  categories,
  showFilters,
  setShowFilters
}: AdvancedLuxuryFiltersProps) => {
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [minRating, setMinRating] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    price: true,
    brands: true,
    colors: true,
    rating: true,
    features: true
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const sortOptions = [
    { value: "featured", label: "Featured First", icon: Crown },
    { value: "popularity", label: "Most Popular", icon: TrendingUp },
    { value: "rating", label: "Highest Rated", icon: Star },
    { value: "price_low", label: "Price: Low to High", icon: null },
    { value: "price_high", label: "Price: High to Low", icon: null },
    { value: "name", label: "Name A-Z", icon: null },
    { value: "newest", label: "Newest First", icon: Sparkles },
  ];

  const luxuryBrands = [
    "Apple", "Samsung", "Sony", "LG", "Nike", "Adidas", "Rolex", "Omega"
  ];

  const colorOptions = [
    { name: "Black", hex: "#000000" },
    { name: "White", hex: "#FFFFFF" },
    { name: "Silver", hex: "#C0C0C0" },
    { name: "Gold", hex: "#FFD700" },
    { name: "Rose Gold", hex: "#E8B4B8" },
    { name: "Blue", hex: "#0066CC" },
    { name: "Red", hex: "#DC2626" },
    { name: "Green", hex: "#059669" },
  ];

  const premiumFeatures = [
    { id: "premium", label: "Premium Quality", icon: Crown },
    { id: "limited", label: "Limited Edition", icon: Sparkles },
    { id: "bestseller", label: "Bestseller", icon: Award },
    { id: "new", label: "New Arrival", icon: TrendingUp },
  ];

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSortBy("featured");
    setPriceRange([0, 2000]);
    setSelectedBrands([]);
    setSelectedColors([]);
    setMinRating(0);
  };

  return (
    <AnimatePresence>
      {showFilters && (
        <motion.div
          className="w-80 flex-shrink-0"
          initial={{ x: -300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -300, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white/90 backdrop-blur-xl border-0 rounded-2xl shadow-2xl p-6 sticky top-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl">
                  <SlidersHorizontal className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Refine Selection
                </h3>
              </div>
            </div>

            {/* Premium Search */}
            <div className="mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search luxury items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-3 bg-white/60 border-gray-200/50 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300"
                />
              </div>
            </div>

            {/* Categories Section */}
            <div className="mb-8">
              <motion.button
                onClick={() => toggleSection('categories')}
                className="flex items-center justify-between w-full mb-4 text-left"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h4 className="text-lg font-medium text-gray-900">Collections</h4>
                {expandedSections.categories ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </motion.button>
              
              <AnimatePresence>
                {expandedSections.categories && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2 overflow-hidden"
                  >
                    <motion.button
                      onClick={() => setSelectedCategory("all")}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
                        selectedCategory === "all"
                          ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                          : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">All Collections</span>
                        <Sparkles className="h-4 w-4" />
                      </div>
                    </motion.button>
                    
                    {categories?.map((category) => (
                      <motion.button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
                          selectedCategory === category.id
                            ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                            : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {category.name}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Price Range Section */}
            <div className="mb-8">
              <motion.button
                onClick={() => toggleSection('price')}
                className="flex items-center justify-between w-full mb-4 text-left"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h4 className="text-lg font-medium text-gray-900">Price Range</h4>
                {expandedSections.price ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </motion.button>
              
              <AnimatePresence>
                {expandedSections.price && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 overflow-hidden"
                  >
                    <div className="px-2">
                      <Slider
                        value={priceRange}
                        onValueChange={setPriceRange}
                        max={2000}
                        min={0}
                        step={50}
                        className="w-full"
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm font-medium text-gray-600">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Brand Selection */}
            <div className="mb-8">
              <motion.button
                onClick={() => toggleSection('brands')}
                className="flex items-center justify-between w-full mb-4 text-left"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h4 className="text-lg font-medium text-gray-900">Luxury Brands</h4>
                {expandedSections.brands ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </motion.button>
              
              <AnimatePresence>
                {expandedSections.brands && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3 overflow-hidden"
                  >
                    {luxuryBrands.map((brand) => (
                      <motion.label
                        key={brand}
                        className="flex items-center gap-3 cursor-pointer group"
                        whileHover={{ scale: 1.02 }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBrands([...selectedBrands, brand]);
                            } else {
                              setSelectedBrands(selectedBrands.filter(b => b !== brand));
                            }
                          }}
                          className="w-5 h-5 rounded border-2 border-gray-300 text-purple-500 focus:ring-purple-500/20"
                        />
                        <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                          {brand}
                        </span>
                      </motion.label>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Color Selection */}
            <div className="mb-8">
              <motion.button
                onClick={() => toggleSection('colors')}
                className="flex items-center justify-between w-full mb-4 text-left"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h4 className="text-lg font-medium text-gray-900">Colors</h4>
                {expandedSections.colors ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </motion.button>
              
              <AnimatePresence>
                {expandedSections.colors && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-4 gap-3 overflow-hidden"
                  >
                    {colorOptions.map((color) => (
                      <motion.button
                        key={color.name}
                        onClick={() => {
                          if (selectedColors.includes(color.name)) {
                            setSelectedColors(selectedColors.filter(c => c !== color.name));
                          } else {
                            setSelectedColors([...selectedColors, color.name]);
                          }
                        }}
                        className={`w-10 h-10 rounded-full border-4 transition-all duration-300 ${
                          selectedColors.includes(color.name)
                            ? "border-purple-500 scale-110"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        style={{ backgroundColor: color.hex }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        title={color.name}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Rating Filter */}
            <div className="mb-8">
              <motion.button
                onClick={() => toggleSection('rating')}
                className="flex items-center justify-between w-full mb-4 text-left"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h4 className="text-lg font-medium text-gray-900">Minimum Rating</h4>
                {expandedSections.rating ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </motion.button>
              
              <AnimatePresence>
                {expandedSections.rating && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2 overflow-hidden"
                  >
                    {[4, 3, 2, 1].map((rating) => (
                      <motion.button
                        key={rating}
                        onClick={() => setMinRating(minRating === rating ? 0 : rating)}
                        className={`w-full flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                          minRating === rating
                            ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white"
                            : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span>& up</span>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sort Options */}
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Sort By</h4>
              <div className="space-y-2">
                {sortOptions.map((option) => (
                  <motion.button
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 flex items-center gap-3 ${
                      sortBy === option.value
                        ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                        : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {option.icon && <option.icon className="h-4 w-4" />}
                    {option.label}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="outline"
                className="w-full border-2 border-gray-200 hover:border-purple-500 hover:text-purple-600 transition-all duration-300"
                onClick={clearAllFilters}
              >
                <X className="h-4 w-4 mr-2" />
                Clear All Filters
              </Button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdvancedLuxuryFilters;
